import { createContext, useContext, useState, useCallback, useRef, useEffect, useMemo } from 'react';
import type { FC, ReactNode } from 'react';
import type { RangeSelection } from './types';
import {
  loadFromStorage,
  saveToStorage,
  type ArticleSnapshot,
} from './selection-history-storage';

interface SelectionHistoryContextType {
  getChunkSelections: (chunkIndex: number) => Map<string, RangeSelection>;
  onChunkSelectionsChange: (chunkIndex: number, selections: Map<string, RangeSelection>) => void;
  clear: () => void;
  hasSelections: boolean;
  hidden: boolean;
  setHidden: (hidden: boolean) => void;
  toggleHidden: () => void;
  revision: number;
}

const SelectionHistoryContext = createContext<SelectionHistoryContextType>({
  getChunkSelections: () => new Map(),
  onChunkSelectionsChange: () => {},
  clear: () => {},
  hasSelections: false,
  hidden: false,
  setHidden: () => {},
  toggleHidden: () => {},
  revision: 0,
});

export const SelectionHistoryProvider: FC<{ articleId: string; children: ReactNode }> = ({
  articleId,
  children,
}) => {
  const currentRef = useRef<ArticleSnapshot>(new Map());
  const [revision, setRevision] = useState(0);
  // hasSelections only flips on the 0 ↔ >0 transition. Per-chunk
  // mutations that don't change emptiness leave it untouched, so the
  // provider doesn't re-render and consumers (every ChunkRenderer in the
  // article) don't get invalidated on every tap. Without this gate, the
  // old `forceUpdate` pattern re-rendered every chunk on every selection
  // change — `memo(ChunkRenderer)` couldn't help because React invalidates
  // all `useContext` consumers when the context value reference changes.
  const [hasSelections, setHasSelections] = useState(false);
  const [hidden, setHidden] = useState(false);

  useEffect(() => {
    const cached = loadFromStorage(articleId);
    if (cached) {
      currentRef.current = cached;
      setHasSelections(cached.size > 0);
      setRevision((r) => r + 1);
    }
  }, [articleId]);

  const persist = useCallback(() => {
    saveToStorage(articleId, currentRef.current);
  }, [articleId]);

  const getChunkSelections = useCallback(
    (chunkIndex: number): Map<string, RangeSelection> => {
      return currentRef.current.get(chunkIndex) ?? new Map();
    },
    []
  );

  const onChunkSelectionsChange = useCallback(
    (chunkIndex: number, selections: Map<string, RangeSelection>) => {
      if (selections.size === 0) {
        currentRef.current.delete(chunkIndex);
      } else {
        currentRef.current.set(chunkIndex, new Map(selections));
      }
      persist();
      const nowHas = currentRef.current.size > 0;
      // Use functional setState so React's bail-out skips the render when
      // the boolean is unchanged.
      setHasSelections((prev) => (prev === nowHas ? prev : nowHas));
    },
    [persist]
  );

  const clear = useCallback(() => {
    if (currentRef.current.size === 0) return;
    currentRef.current = new Map();
    persist();
    setHidden(false);
    setHasSelections(false);
    // Revision bump re-syncs each chunk's local useSelection state from
    // its (now-empty) initialSelections snapshot.
    setRevision((r) => r + 1);
  }, [persist]);

  const toggleHidden = useCallback(() => {
    setHidden((h) => !h);
  }, []);

  const value = useMemo<SelectionHistoryContextType>(() => ({
    getChunkSelections,
    onChunkSelectionsChange,
    clear,
    hasSelections,
    hidden,
    setHidden,
    toggleHidden,
    revision,
  }), [getChunkSelections, onChunkSelectionsChange, clear, hasSelections, hidden, toggleHidden, revision]);

  return (
    <SelectionHistoryContext.Provider value={value}>
      {children}
    </SelectionHistoryContext.Provider>
  );
};

export const useSelectionHistory = () => useContext(SelectionHistoryContext);
